import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { transformRoute, useTranslate } from "helpers";
import { Pagination } from "services/search/types";
import { DataWithPagination } from "services/api/types";
import {
  NotificationLog,
  NotificationLogSearchFormValues,
} from "services/api/notifications/types";
import { getNotificationLogsAPI } from "services/api/notifications/api";
import {
  initialNotificationPagination,
  initialNotificationLogSearchForm,
} from "services/api/notifications/consts";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation/routes";
import NotificationLogFilters from "./components/NotificationLogFilters";
import { Routes } from "../../navigation/routes";
import TableSearch from "ui/components/Table/TableSearch/TableSearch";
import PageHeader from "ui/components/PageHeader/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { NOTIFICATION_LOG_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { NOTIFICATION_LOG_COLUMNS } from "./consts";

type NotificationLogPageCmp = React.FC & { route: string };

const NotificationLogPage: NotificationLogPageCmp = () => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<DataWithPagination<NotificationLog>>();
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>(initialNotificationPagination);
  const [filters, setFilters] = useState<NotificationLogSearchFormValues>(
    initialNotificationLogSearchForm
  );
  const navigate = useNavigate();

  const fetchResults = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getNotificationLogsAPI(pagination, filters);
      setSearchResults(result);
    } catch {
      // error handled by createApiCall
    }
    setIsLoading(false);
  }, [pagination, filters]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handlePaginationChange = (newPagination: Pagination) => {
    setPagination(newPagination);
  };

  // Nova pretraga kreće od prve stranice — inače bi na 3. stranici ispalo da nema rezultata.
  const handleSearchChange = useCallback((newFilters: NotificationLogSearchFormValues) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleVehicleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, vehicleId: string) => {
      e.stopPropagation();
      navigate(transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id: vehicleId }));
    },
    [navigate]
  );

  const columns = useMemo(
    () => NOTIFICATION_LOG_COLUMNS(t, handleVehicleClick),
    [t, handleVehicleClick]
  );

  return (
    <div>
      <div className="bg-white rounded-t-md border border-b mb-2">
        <PageHeader>
          <div className="flex items-center justify-between">
            <div>
              <PageTitle
                title={NOTIFICATION_LOG_PAGE_TITLE.text}
                icon={NOTIFICATION_LOG_PAGE_TITLE.icon}
                iconColor={NOTIFICATION_LOG_PAGE_TITLE.iconColor}
              />
              <Breadcrumb />
            </div>
          </div>
        </PageHeader>
      </div>

      <div className="px-4">
        <NotificationLogFilters onSearchChange={handleSearchChange} />

        <TableSearch
          data={searchResults?.data ?? []}
          columns={columns}
          pagination={searchResults?.pagination ?? pagination}
          onPaginationChange={handlePaginationChange}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

NotificationLogPage.route = Routes.NotificationLogSearchPageRouter;

export default NotificationLogPage;
